/**
 * usePreferenceManager Hook
 *
 * React hook for managing user preferences (theme, mood, sizing).
 * Provides easy-to-use functions for updating preferences.
 * Preference data is cached in localStorage and automatically loaded on mount.
 *
 * NOTE: Profile image is now managed separately via user.profile_image (not in preferences)
 */

import { useState, useCallback, useEffect } from 'react';
import { Action } from '@/business/types/action.types';
import { actionOrchestrator } from '@/business/orchestrator/ActionOrchestrator';
import type {
  UpdateUserPreferencesInput,
  UpdateUserPreferencesOutput,
  GetPreferencesOutput,
} from '@/types/generated/processor-types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { logger } from '@/utils/logger';
import StorageService from '@/utils/storage';
import { StorageKey } from '@/types/storage';
import { UserPreferences } from '@/types/preferences.types';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';

// ============================================================================
// TYPES
// ============================================================================

/**
 * User preferences to update
 */
export interface UserPreferencesUpdate {
  /** Theme identifier */
  theme?: string;
  /** User mood/status text */
  mood?: string;
  /** UI sizing preference (small/medium/large) */
  sizing?: string;
  /** Playful interaction theme identifier */
  playful_theme?: string;
  /** Playful theme sound toggle ("on"/"off") */
  playful_sound?: string;
}

/**
 * Profile manager state
 */
export interface PreferenceManagerState {
  /** Whether an operation is in progress */
  isLoading: boolean;
  /** Current preference data */
  preferences: UserPreferences | null;
  /** Error message if operation failed */
  error: string | null;
}

/**
 * Profile manager actions
 */
export interface PreferenceManagerActions {
  /** Update user preferences (theme/mood/sizing) */
  updatePreferences: (preferences: UserPreferencesUpdate) => Promise<boolean>;
  /** Sync preferences to local cache and backend (efficient, non-blocking) */
  syncPreferences: (preferences: UserPreferencesUpdate) => void;
  /** Refresh preference data from backend */
  refreshPreferences: () => Promise<void>;
  /** Clear error state */
  clearError: () => void;
}

/**
 * Profile manager hook return type
 */
export interface UsePreferenceManagerReturn {
  state: PreferenceManagerState;
  actions: PreferenceManagerActions;
}

// ============================================================================
// MODULE-LEVEL API (callable outside React, for initialization)
// ============================================================================

/**
 * Returns the cached preferences from storage.
 *
 * Exported so that UI providers (ThemeContext, SizingContext) can read the
 * initial value through the preference owner's API rather than touching
 * StorageService directly. They are NOT allowed to write to this key.
 */
export function getInitialPreferences(): UserPreferences | null {
  return StorageService.get<UserPreferences>(StorageKey.USER_PREFERENCES);
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for managing user preferences
 *
 * Provides state and actions for:
 * - Updating user preferences (theme, mood, sizing)
 * - Retrieving preference data
 *
 * @returns Preference manager state and actions
 *
 * @example
 * ```typescript
 * function PreferenceSettings() {
 *   const { state, actions } = usePreferenceManager();
 *
 *   const handleSave = async () => {
 *     await actions.updatePreferences({
 *       theme: selectedTheme,
 *       mood: currentMood,
 *       sizing: selectedSize
 *     });
 *   };
 *
 *   return (
 *     <div>
 *       {state.isLoading && <div>Saving...</div>}
 *       {state.error && <div>Error: {state.error}</div>}
 *       {state.preferences && (
 *         <div>
 *           <div>Theme: {state.preferences.theme}</div>
 *           <div>Mood: {state.preferences.mood}</div>
 *           <div>Sizing: {state.preferences.sizing}</div>
 *         </div>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePreferenceManager(): UsePreferenceManagerReturn {
  // State
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Load cached preference data from localStorage on mount
   * Preferences are automatically cached when GET_PREFERENCES action succeeds
   */
  useEffect(() => {
    const cachedPreferences = StorageService.get<UserPreferences>(
      StorageKey.USER_PREFERENCES
    );
    if (cachedPreferences) {
      logger.info('Loaded cached preferences from localStorage');
      setPreferences(cachedPreferences);
    }
  }, []);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Refresh preference data from backend
   * Fetches latest preferences and automatically caches to localStorage via GET_PREFERENCES side effect
   */
  const refreshPreferences = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      logger.info('Fetching user preferences...');

      const result = await actionOrchestrator.execute<
        Record<string, never>,
        GetPreferencesOutput
      >(
        Action.GET_PREFERENCES,
        {},
        {
          toast: false, // Silent
        }
      );

      if (result.code !== ResponseCode.SUCCESS || !result.data) {
        logger.error('Failed to get preferences', { code: result.code });
        setError('Failed to load preferences');
        return;
      }

      // Extract preference fields (no profile_image - it's in user now)
      const preferencesData: UserPreferences = {
        theme: result.data.profile?.theme,
        mood: result.data.profile?.mood,
        sizing: result.data.profile?.sizing,
        playful_theme: result.data.profile?.playful_theme,
        playful_sound: result.data.profile?.playful_sound,
      };

      setPreferences(preferencesData);
      logger.info('User preferences retrieved successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Preference refresh failed', { error: err });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update user preferences (theme and/or mood and/or sizing)
   */
  const updatePreferences = useCallback(
    async (preferences: UserPreferencesUpdate): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        logger.info('Updating user preferences...', preferences);

        const input: UpdateUserPreferencesInput = {
          theme: preferences.theme,
          mood: preferences.mood,
          sizing: preferences.sizing,
          playful_theme: preferences.playful_theme,
          playful_sound: preferences.playful_sound,
        };

        const result = await actionOrchestrator.execute<
          UpdateUserPreferencesInput,
          UpdateUserPreferencesOutput
        >(Action.UPDATE_USER_PREFERENCES, input, {
          toast: {
            success: 'Preferences saved',
            error: 'Failed to save preferences',
          },
        });

        if (result.code !== ResponseCode.SUCCESS) {
          logger.error('Failed to update preferences', { code: result.code });
          setError('Failed to update preferences');
          return false;
        }

        logger.info('User preferences updated successfully');

        // Refresh preferences to get latest data and sync to localStorage
        await refreshPreferences();

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        logger.error('Preference update failed', { error: err });
        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshPreferences]
  );

  /**
   * Sync preferences to local cache and backend (efficient, non-blocking)
   *
   * This method is optimized for UX:
   * 1. Immediately updates local state and localStorage (synchronous, fast)
   * 2. Asynchronously pushes to backend (fire-and-forget, non-blocking)
   * 3. No refetch needed (local state already updated)
   *
   * Use this when you want to persist preferences without blocking the UI
   * (e.g., on popup close, auto-save, etc.)
   */
  const syncPreferences = useCallback(
    (prefs: UserPreferencesUpdate): void => {
      // Step 1: Update local state immediately (synchronous)
      if (preferences) {
        const updatedPreferences: UserPreferences = {
          ...preferences,
          ...(prefs.theme !== undefined && { theme: prefs.theme }),
          ...(prefs.mood !== undefined && { mood: prefs.mood }),
          ...(prefs.sizing !== undefined && { sizing: prefs.sizing }),
          ...(prefs.playful_theme !== undefined && {
            playful_theme: prefs.playful_theme,
          }),
          ...(prefs.playful_sound !== undefined && {
            playful_sound: prefs.playful_sound,
          }),
        };

        // Update state immediately
        setPreferences(updatedPreferences);

        // Update localStorage cache immediately
        StorageService.set(StorageKey.USER_PREFERENCES, updatedPreferences);

        // Update React Query cache — ThemeContext/SizingContext subscribe via usePreferencesQuery
        queryClient.setQueryData(
          queryKeys.userPreferences.all(),
          updatedPreferences
        );

        logger.info('Preferences synced to local cache', prefs);
      }

      // Step 2: Push to backend asynchronously (fire-and-forget)
      // Don't await, don't block UI, no refetch needed
      (async () => {
        try {
          const input: UpdateUserPreferencesInput = {
            theme: prefs.theme,
            mood: prefs.mood,
            sizing: prefs.sizing,
            playful_theme: prefs.playful_theme,
            playful_sound: prefs.playful_sound,
          };

          const result = await actionOrchestrator.execute<
            UpdateUserPreferencesInput,
            UpdateUserPreferencesOutput
          >(Action.UPDATE_USER_PREFERENCES, input, {
            toast: false, // Silent - local state already updated
          });

          if (result.code === ResponseCode.SUCCESS) {
            logger.info('Preferences synced to backend', prefs);
          } else {
            logger.warn('Failed to sync preferences to backend', {
              code: result.code,
            });
            // Don't throw - local state is already updated, backend sync is optional
          }
        } catch (err) {
          logger.warn(
            'Backend preference sync failed (local cache is up-to-date)',
            {
              error: err,
            }
          );
          // Silent failure - user's local experience is not affected
        }
      })();
    },
    [preferences]
  );

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    state: {
      isLoading,
      preferences,
      error,
    },
    actions: {
      updatePreferences,
      syncPreferences,
      refreshPreferences,
      clearError,
    },
  };
}
